import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Button, PanelCard, Spinner, Track } from '../../school_panel/components/ui'
import { BookOpen, Bot, Check, ChevronDown, ChevronUp, Play, Plus, RotateCw, Square, X } from 'lucide-react'
import { useAuth } from '../../utils/Auth/AuthProvider'
import { useUserInfo } from '../../react-query/queries'
import { request } from '../../utils/remote'
import { usePipelineEngine } from '../../hooks/usePipelineEngine'
import type { PipelineQuestion } from '../../hooks/usePipelineEngine'
import { apiErrorText } from '../../school_panel/lib/apiError'
import PipelineSummary from './PipelineSummary'
import CollapsedQuestionCard from './CollapsedQuestionCard'
import { useExamSessions } from '../../store/ExamSessionStore'

interface Props {
    examId: string | null
    inputQuestions: any[]
    courseIds: string[]
    gradeId?: string
    subjectId?: string
    sessionId: string
    aiKnowledgeOnly?: boolean
    // School mode: forces useOptions=false (all chapters for subject, not chapter_option),
    // skips chapter-type detection, and treats the Gemini key as optional since the
    // school path uses the server-side aiBatchGenerate endpoint.
    schoolMode?: boolean
    /** `info.chapters[i]` is the chapter the pipeline assigned to question i (shown in the review step; never sent to the server). */
    onSave: (questionsWithMeta: any[], info?: { chapters: Array<string | null> }) => Promise<any>
}

const phaseLabels: Record<string, string> = {
    idle: 'Ready',
    detecting: 'Detecting chapter type…',
    chapters: 'Assigning chapters',
    rag: 'Searching textbooks',
    explanation: 'Generating explanations',
    done: 'Complete',
    error: 'Error',
}

const ExamProcessingPipeline: React.FC<Props> = ({
    examId,
    inputQuestions,
    courseIds,
    gradeId,
    subjectId,
    sessionId,
    aiKnowledgeOnly = false,
    schoolMode = false,
    onSave,
}) => {
    const { token } = useAuth()
    const { data: userData } = useUserInfo()
    const [geminiKey, setGeminiKey] = useState<string | null>(null)
    const [fetchedRags, setAllRags] = useState<any[]>([])
    // School mode: the subject's books the teacher keeps for this exam (null until the list loads).
    // Starts as the exam grade's book(s); a national exam (no gradeId) starts with every grade's.
    const [chosenBookIds, setChosenBookIds] = useState<string[] | null>(null)
    const [booksConfirmed, setBooksConfirmed] = useState(!schoolMode)
    const [isLoadingRags, setIsLoadingRags] = useState(false)
    const [useOptions, setUseOptions] = useState<boolean | null>(schoolMode ? false : null)
    const [showChapterChoice, setShowChapterChoice] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [saveError, setSaveError] = useState<string | null>(null)
    const [showQuestions, setShowQuestions] = useState(false)
    const [pipelineStarted, setPipelineStarted] = useState(false)
    const hasAutoStarted = useRef(false)
    const [bookPickerOpen, setBookPickerOpen] = useState(false)
    const [bookPickerIndices, setBookPickerIndices] = useState<number[]>([])
    const [selectedBookIds, setSelectedBookIds] = useState<string[]>([])

    const { updateSession } = useExamSessions()
    const pipeline = usePipelineEngine(geminiKey)
    const { questions, progress, results, runPipeline, runAIKnowledgeOnly, cancel, retryFailed, retryNoContextAllBooks, retrySpecificBooks, useAIKnowledge, reassignChapter, detectChapterType, chapterCatalog, editQuestionMeta, setQuestionChapter } = pipeline

    // Sync pipeline progress to session store for tab indicators
    useEffect(() => {
        if (!sessionId) return
        updateSession(sessionId, {
            pipelinePhase: progress.phase,
            pipelineTotal: progress.total,
            pipelineCompleted: progress.completed,
            pipelineFailed: progress.failed,
        })
    }, [progress.phase, progress.total, progress.completed, progress.failed, sessionId])

    // Load Gemini key
    useEffect(() => {
        if (!token) return
        (async () => {
            try {
                const data = await request('/api/user/gemini-key/', { method: 'GET', headers: { Authorization: `Bearer ${token}` } })
                if (data?.has_key && data?.api_key) setGeminiKey(String(data.api_key))
            } catch { /* ignore */ }
        })()
    }, [token])

    // Load RAGs (textbooks). University: filter by course_ids. School: every grade's books for the
    // subject; the teacher confirms which to use (the exam grade's are preselected) before processing.
    useEffect(() => {
        if (!token) return
        if (!schoolMode && !courseIds.length) return
        setIsLoadingRags(true)
        ;(async () => {
            try {
                const qp = new URLSearchParams()
                if (schoolMode) {
                    qp.set('type', 'school')
                    if (subjectId) qp.set('subject_id', String(subjectId))
                } else {
                    qp.set('course_ids', courseIds.join(','))
                }
                const data = await request(`/api/textbook/list/?${qp.toString()}`, { headers: { Authorization: `Bearer ${token}` } })
                const books = data?.books || []
                setAllRags(books)
                if (schoolMode) {
                    const forGrade = gradeId ? books.filter((b: any) => (b.school_grade_ids || []).map(String).includes(String(gradeId))) : books
                    setChosenBookIds(forGrade.map((b: any) => String(b.id)))
                }
            } catch (e) {
                setAllRags([])
            } finally {
                setIsLoadingRags(false)
            }
        })()
    }, [token, courseIds, schoolMode, subjectId, gradeId])

    const allRags = useMemo(
        () => (schoolMode ? fetchedRags.filter((b: any) => (chosenBookIds ?? []).includes(String(b.id))) : fetchedRags),
        [schoolMode, fetchedRags, chosenBookIds],
    )
    // Chapters come from the exam grade plus the grades of any added books; a national exam
    // (no gradeId) keeps every grade.
    const gradeIds = useMemo(() => {
        if (!schoolMode || !gradeId) return undefined
        const ids = new Set<string>([String(gradeId)])
        for (const b of allRags) for (const g of (b.school_grade_ids || [])) ids.add(String(g))
        return Array.from(ids)
    }, [schoolMode, gradeId, allRags])

    // Auto-detect chapter type — skipped in school mode (chapters live directly under subject, not as options)
    useEffect(() => {
        if (schoolMode) return
        // AI-knowledge-only path: never show the chapter-type prompt when the
        // course has no linked textbooks (chapters come from the catalog
        // endpoint). If books exist, the normal pipeline runs and still needs
        // chapter-type detection.
        if (aiKnowledgeOnly && (isLoadingRags || allRags.length === 0)) return
        if (!token || !courseIds.length || useOptions !== null) return
        ;(async () => {
            try {
                const detected = await detectChapterType(courseIds)
                if (detected === 'chapter_option') {
                    setUseOptions(true)
                } else if (detected === 'chapter') {
                    setUseOptions(false)
                } else {
                    setShowChapterChoice(true)
                }
            } catch {
                setShowChapterChoice(true)
            }
        })()
    }, [token, courseIds, useOptions, detectChapterType, aiKnowledgeOnly, isLoadingRags, allRags])

    const startPipeline = useCallback(async () => {
        // School mode uses the server-side aiBatchGenerate path, so a per-user Gemini key isn't required.
        if ((!geminiKey && !schoolMode) || useOptions === null) return
        setPipelineStarted(true)
        const selectedRagIds = allRags.map((b: any) => String(b.id))
        await runPipeline(inputQuestions, {
            courseIds,
            examId,
            gradeId,
            gradeIds,
            subjectId,
            useOptions,
            allRags,
            selectedRagIds,
        })
    }, [geminiKey, useOptions, allRags, inputQuestions, courseIds, examId, gradeId, gradeIds, subjectId, runPipeline, schoolMode])

    // Auto-start pipeline when everything is ready
    useEffect(() => {
        if (hasAutoStarted.current) return
        if (pipelineStarted) return
        // Skip geminiKey check in schoolMode (server-side key is used via aiBatchGenerate)
        if ((!geminiKey && !schoolMode) || isLoadingRags || !inputQuestions.length) return
        // School mode waits for the teacher to confirm the books.
        if (schoolMode && !booksConfirmed) return

        // AI-knowledge-only path: triggered when the spec's strict condition holds
        // (national exam mode + requires_institution=false + requires_department=false)
        // AND the course has no linked textbooks. Chapters still get assigned; only RAG
        // is skipped. Per the design spec's auto-start rule this path must NOT wait on
        // the chapter-type prompt — default to plain chapters when undetected.
        if (aiKnowledgeOnly && allRags.length === 0) {
            hasAutoStarted.current = true
            setPipelineStarted(true)
            runAIKnowledgeOnly(inputQuestions, {
                courseIds,
                examId,
                useOptions: useOptions ?? false,
                gradeId,
                subjectId,
            })
            return
        }

        // Normal pipeline (chapters + RAG + explanation) — needs books and a chapter type.
        if (useOptions === null) return
        // School mode may run with no books (confirmed by the teacher): the AI uses its own knowledge.
        if (!allRags.length && !schoolMode) return
        hasAutoStarted.current = true
        startPipeline()
    }, [geminiKey, isLoadingRags, useOptions, inputQuestions, allRags, pipelineStarted, startPipeline, aiKnowledgeOnly, runAIKnowledgeOnly, courseIds, examId, gradeId, subjectId, schoolMode, booksConfirmed])

    const handleSave = useCallback(async () => {
        if (isSaving) return
        setIsSaving(true)
        setSaveError(null)
        try {
            // Build payload from pipeline questions — only succeeded + noContext
            const saveable = questions.filter(q => q._status === 'explanation_done' || q._status === 'no_context')
            const payload = saveable.map((q: PipelineQuestion) => {
                const out: any = { ...q }
                // Clean pipeline tracking fields
                delete out._status
                delete out._retries
                delete out._course_id
                delete out._ragHits
                delete out._error
                delete out._chapterAssignment
                // Normalize field names for backend: 'question' -> 'ques', choices array -> choice_a/b/c/d
                if (out.question && !out.ques) {
                    out.ques = out.question
                }
                delete out.question
                // Flatten choices array into choice_a/b/c/d/e if present
                if (Array.isArray(out.choices) && out.choices.length > 0) {
                    const labels = ['choice_a', 'choice_b', 'choice_c', 'choice_d', 'choice_e']
                    out.choices.forEach((c: string, i: number) => { if (i < labels.length && !out[labels[i]]) out[labels[i]] = c })
                }
                delete out.choices
                // Handle corrected answer — save as label (A, B, C, D)
                if (q.corrected_answer) {
                    const label = q.corrected_answer.trim().toUpperCase()
                    if (['A', 'B', 'C', 'D', 'E'].includes(label)) {
                        out.ans = label
                    } else {
                        // Fallback: if it's already full text or something else, use it
                        out.ans = q.corrected_answer
                    }
                    out.corrected_answer = q.corrected_answer
                } else if (!out.ans && q.ans) {
                    out.ans = q.ans
                }
                // Ensure top-level fields the backend expects
                if (!out.explanation && q.explanation) out.explanation = q.explanation
                if (!out.reference) {
                    const refs = q.references || []
                    out.reference = Array.isArray(refs) ? refs.join('<br>') : (refs || '')
                }
                // Set ragAssignment for backward compatibility with save handler
                out.ragAssignment = {
                    status: 'done',
                    acceptedChapter: true,
                    acceptedExplanation: true,
                    accepted: true,
                    geminiSuggestion: {
                        explanation: q.explanation || '',
                        references: q.references || [],
                        corrected_answer: q.corrected_answer || '',
                    }
                }
                return out
            })
            await onSave(payload, { chapters: saveable.map((q: PipelineQuestion) => q._chapterAssignment?.chapter_name ?? null) })
        } catch (err) {
            setSaveError(apiErrorText(err, 'The questions could not be saved. Try again.'))
        } finally {
            setIsSaving(false)
        }
    }, [isSaving, questions, onSave])

    const handleRetryFailed = useCallback(() => {
        const selectedRagIds = allRags.map((b: any) => String(b.id))
        retryFailed(allRags, selectedRagIds)
    }, [allRags, retryFailed])

    const handleRetryNoContextAllBooks = useCallback((indices: number[]) => {
        retryNoContextAllBooks(indices, allRags)
    }, [allRags, retryNoContextAllBooks])

    const handleSearchSpecificBooks = useCallback((index: number) => {
        setBookPickerIndices([index])
        setSelectedBookIds([])
        setBookPickerOpen(true)
    }, [])

    const handleSearchSpecificBooksBulk = useCallback((indices: number[]) => {
        setBookPickerIndices(indices)
        setSelectedBookIds([])
        setBookPickerOpen(true)
    }, [])

    const handleBookPickerConfirm = useCallback(() => {
        if (bookPickerIndices.length === 0 || selectedBookIds.length === 0) return
        setBookPickerOpen(false)
        retrySpecificBooks(bookPickerIndices, selectedBookIds, allRags)
        setBookPickerIndices([])
        setSelectedBookIds([])
    }, [bookPickerIndices, selectedBookIds, allRags, retrySpecificBooks])

    const handleReassignChapter = useCallback((index: number) => {
        if (useOptions === null) return
        reassignChapter(index, courseIds, useOptions, allRags)
    }, [courseIds, useOptions, allRags, reassignChapter])

    const handleReassignChapterBulk = useCallback((indices: number[]) => {
        if (useOptions === null) return
        indices.forEach(i => reassignChapter(i, courseIds, useOptions, allRags))
    }, [courseIds, useOptions, allRags, reassignChapter])

    const isProcessing = ['detecting', 'chapters', 'rag', 'explanation'].includes(progress.phase)
    const isDone = progress.phase === 'done'
    const isError = progress.phase === 'error'

    // Chapter type choice dialog
    if (showChapterChoice && useOptions === null) {
        return (
            <PanelCard className="mx-auto max-w-md text-center">
                <span aria-hidden className="mx-auto mb-3 grid h-[52px] w-[52px] place-items-center rounded-[14px] bg-mist text-slate"><Bot size={24} strokeWidth={1.8} /></span>
                <h3 className="font-display text-[17px] font-bold text-ink">Chapter type</h3>
                <p className="mt-1.5 text-sm text-muted">Both chapter types are available. Which would you like to use?</p>
                <div className="mt-5 flex flex-wrap justify-center gap-2.5">
                    <Button onClick={() => { setUseOptions(true); setShowChapterChoice(false) }}>
                        Chapter options
                    </Button>
                    <Button variant="ghost" onClick={() => { setUseOptions(false); setShowChapterChoice(false) }}>
                        Regular chapters
                    </Button>
                </div>
            </PanelCard>
        )
    }

    // Loading state — in schoolMode, geminiKey isn't required (server-side path handles auth)
    if ((!geminiKey && !schoolMode) || isLoadingRags || useOptions === null) {
        return (
            <div className="flex flex-col items-center justify-center gap-4 rounded-[20px] border border-line bg-surface py-16 shadow-card">
                <Spinner size="md" />
                <p className="text-sm font-medium text-muted">
                    {(!geminiKey && !schoolMode)
                        ? 'Loading AI key…'
                        : isLoadingRags
                            ? (aiKnowledgeOnly ? 'Checking textbooks…' : 'Loading textbooks…')
                            : 'Detecting chapter type…'}
                </p>
            </div>
        )
    }

    if (schoolMode && !booksConfirmed && !pipelineStarted) {
        const chosen = new Set(chosenBookIds ?? [])
        const kept = fetchedRags.filter((b: any) => chosen.has(String(b.id)))
        const others = fetchedRags.filter((b: any) => !chosen.has(String(b.id)))
        const gradeOf = (b: any) => (b.grade ? `Grade ${b.grade}` : 'No grade')
        return (
            <PanelCard
                title={<h3 className="font-display text-[17px] font-bold text-ink">Books for this exam</h3>}
                subtitle="Chapters, explanations and references come only from these books. Add another grade's book when the exam covers more than one grade.">
                <ul aria-label="Books to use" className="space-y-2">
                    {kept.length === 0 && (
                        <li className="rounded-[12px] bg-warning-bg px-3.5 py-3 text-sm text-warning-ink">
                            No textbook is linked to this grade yet. Add a book below, or continue and the AI will rely on its own knowledge.
                        </li>
                    )}
                    {kept.map((b: any) => (
                        <li key={b.id} className="flex items-center gap-3 rounded-[12px] border border-line px-3.5 py-2.5">
                            <BookOpen size={18} strokeWidth={1.8} aria-hidden className="flex-none text-slate" />
                            <span className="min-w-0 flex-1">
                                <span className="block truncate text-sm font-semibold text-ink">{b.title}</span>
                                <span className="text-xs text-muted">{gradeOf(b)}{b.subject ? ` · ${b.subject}` : ''}</span>
                            </span>
                            <button type="button" aria-label={`Remove ${b.title}`}
                                onClick={() => setChosenBookIds((ids) => (ids ?? []).filter((x) => x !== String(b.id)))}
                                className="grid h-8 w-8 flex-none place-items-center rounded-[9px] text-muted hover:bg-danger-bg hover:text-danger-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                                <X size={16} aria-hidden />
                            </button>
                        </li>
                    ))}
                </ul>
                {others.length > 0 && (
                    <div className="mt-4">
                        <p className="mb-2 text-[13px] font-semibold text-ink">Add a book from another grade</p>
                        <div className="flex flex-wrap gap-2" role="group" aria-label="Other books">
                            {others.map((b: any) => (
                                <button key={b.id} type="button"
                                    onClick={() => setChosenBookIds((ids) => [...(ids ?? []), String(b.id)])}
                                    className="inline-flex min-h-[36px] items-center gap-1.5 rounded-full border border-line-strong bg-surface px-3 text-[13px] text-ink hover:border-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                                    <Plus size={14} aria-hidden />{b.title} <span className="text-muted">· {gradeOf(b)}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                <div className="mt-5 flex justify-end">
                    <Button icon={<Play size={16} strokeWidth={1.8} aria-hidden />} onClick={() => setBooksConfirmed(true)}>
                        Process {inputQuestions.length} {inputQuestions.length === 1 ? 'question' : 'questions'} with {kept.length} {kept.length === 1 ? 'book' : 'books'}
                    </Button>
                </div>
            </PanelCard>
        )
    }

    const showAIKnowledgeBanner = aiKnowledgeOnly && allRags.length === 0
    return (
        <div className="space-y-[18px]">
            {showAIKnowledgeBanner && (
                <div className="rounded-[14px] bg-warning-bg px-4 py-3 text-sm font-medium text-warning-ink">
                    No textbooks linked for this course — generating explanations from AI knowledge.
                </div>
            )}
            {/* Processing progress */}
            {isProcessing && (
                <PanelCard
                    title={
                        <div className="flex items-center gap-3">
                            <span aria-hidden className="grid h-10 w-10 flex-none place-items-center rounded-[12px] bg-accent text-navy"><Bot size={20} strokeWidth={1.8} className="motion-safe:animate-pulse" /></span>
                            <div className="min-w-0">
                                <h3 className="font-display text-[17px] font-bold tracking-[-0.01em] text-ink">Processing {inputQuestions.length} questions</h3>
                                <p className="mt-0.5 text-[13px] text-muted" aria-live="polite">{progress.message}</p>
                            </div>
                        </div>
                    }
                    actions={
                        <Button size="sm" variant="ghost" icon={<Square size={14} strokeWidth={1.8} aria-hidden />} onClick={cancel}>
                            Cancel
                        </Button>
                    }
                >
                    {/* Phase progress bars. AI-knowledge mode skips RAG but still assigns chapters. */}
                    <div className="space-y-3">
                        {(aiKnowledgeOnly ? (['chapters', 'explanation'] as const) : (['chapters', 'rag', 'explanation'] as const)).map(phase => {
                            const isActive = progress.phase === phase
                            const isPast = ['chapters', 'rag', 'explanation'].indexOf(progress.phase) > ['chapters', 'rag', 'explanation'].indexOf(phase)
                            const pct = isActive && progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : (isPast ? 100 : 0)
                            return (
                                <div key={phase} className="flex items-center gap-3">
                                    <div className={`w-28 flex-none text-[13px] font-semibold sm:w-40 ${isActive ? 'text-ink' : isPast ? 'text-success' : 'text-muted'}`}>
                                        {phaseLabels[phase]}
                                    </div>
                                    <Track className="min-w-0 flex-1" value={pct} tone={isPast ? 'success' : isActive ? 'accent' : 'slate'} label={phaseLabels[phase]} />
                                    <div className={`w-12 flex-none text-right font-mono text-xs font-semibold ${isActive ? 'text-ink' : isPast ? 'text-success' : 'text-muted'}`}>
                                        {isActive ? `${progress.completed}/${progress.total}` : isPast ? <><Check size={14} strokeWidth={2.2} aria-hidden className="ml-auto" /><span className="sr-only">Done</span></> : '—'}
                                    </div>
                                </div>
                            )
                        })}
                    </div>

                    {progress.failed > 0 && (
                        <div className="mt-3 text-xs font-semibold text-danger-ink">
                            {progress.failed} question(s) failed so far
                        </div>
                    )}
                </PanelCard>
            )}

            {/* Error state */}
            {isError && (
                <div className="space-y-4 rounded-[20px] border border-danger/30 bg-danger-bg p-[22px] text-center">
                    <p className="font-semibold text-danger-ink">{progress.message}</p>
                    <Button variant="danger" icon={<RotateCw size={16} strokeWidth={1.8} aria-hidden />} onClick={startPipeline}>
                        Retry processing
                    </Button>
                </div>
            )}

            {/* Done state — show summary */}
            {isDone && saveError && (
                <p role="alert" className="rounded-[12px] border border-danger/30 bg-danger-bg px-3.5 py-2.5 text-sm text-danger-ink">{saveError}</p>
            )}
            {isDone && (
                <PipelineSummary
                    questions={questions}
                    results={results}
                    onRetryFailed={handleRetryFailed}
                    onRetryNoContextAllBooks={handleRetryNoContextAllBooks}
                    onUseAIKnowledge={useAIKnowledge}
                    onSearchSpecificBooks={handleSearchSpecificBooks}
                    onSearchSpecificBooksBulk={handleSearchSpecificBooksBulk}
                    onReassignChapter={handleReassignChapter}
                    onReassignChapterBulk={handleReassignChapterBulk}
                    onSave={handleSave}
                    isSaving={isSaving}
                    catalog={chapterCatalog}
                    onSetChapter={(i: number, entry: any) => setQuestionChapter(i, entry, !!useOptions)}
                    onEditMeta={editQuestionMeta}
                />
            )}

            {/* Idle — not yet started (shouldn't normally show due to auto-start) */}
            {progress.phase === 'idle' && !pipelineStarted && (
                <div className="space-y-4 rounded-[20px] border border-line bg-surface py-12 text-center shadow-card">
                    <span aria-hidden className="mx-auto grid h-[52px] w-[52px] place-items-center rounded-[14px] bg-mist text-slate"><Bot size={24} strokeWidth={1.8} /></span>
                    <p className="font-medium text-muted">Ready to process {inputQuestions.length} questions</p>
                    <Button icon={<Play size={16} strokeWidth={1.8} aria-hidden />} onClick={startPipeline}>
                        Start processing
                    </Button>
                </div>
            )}

            {/* Live question list (collapsed) */}
            {(isProcessing || isDone) && questions.length > 0 && (
                <div className="overflow-hidden rounded-[20px] border border-line bg-surface shadow-card">
                    <button
                        type="button"
                        onClick={() => setShowQuestions(!showQuestions)}
                        aria-expanded={showQuestions}
                        className="flex w-full items-center justify-between px-[22px] py-4 text-left transition-colors hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent"
                    >
                        <span className="font-display text-[15px] font-bold text-ink">
                            All questions <span className="font-mono text-sm font-medium text-muted">({questions.length})</span>
                        </span>
                        {showQuestions ? <ChevronUp size={18} strokeWidth={1.8} aria-hidden className="text-muted" /> : <ChevronDown size={18} strokeWidth={1.8} aria-hidden className="text-muted" />}
                    </button>
                    {showQuestions && (
                        <div className="max-h-[500px] space-y-2 overflow-y-auto border-t border-hairline p-3">
                            {questions.map((q, i) => (
                                <CollapsedQuestionCard key={i} q={q} index={i} />
                            ))}
                        </div>
                    )}
                </div>
            )}
            {/* Book Picker Dialog */}
            {bookPickerOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-navy-deep/60 p-4" onClick={() => setBookPickerOpen(false)}>
                    <div role="dialog" aria-modal="true" aria-labelledby="book-picker-title" className="flex max-h-[80vh] w-full max-w-lg flex-col rounded-[20px] border border-line bg-surface shadow-card" onClick={e => e.stopPropagation()}>
                        <div className="border-b border-line px-6 py-4">
                            <h3 id="book-picker-title" className="font-display text-[17px] font-bold text-ink">Select books to search</h3>
                            <p className="mt-1 text-xs text-muted">{bookPickerIndices.length === 1 ? `Choose books to search for Q${bookPickerIndices[0] + 1}` : `Choose books to search for ${bookPickerIndices.length} questions`}. 5 results per book will be retrieved.</p>
                        </div>
                        <div className="flex-1 space-y-2 overflow-y-auto px-6 py-3">
                            {allRags.length === 0 && <p className="text-sm text-muted">No books available.</p>}
                            {allRags.map((rag: any) => {
                                const id = String(rag.id)
                                const checked = selectedBookIds.includes(id)
                                return (
                                    <label key={id} className={`flex cursor-pointer items-start gap-3 rounded-[12px] border p-3 transition-colors ${checked ? 'border-slate bg-mist' : 'border-line hover:bg-surface-2'}`}>
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => setSelectedBookIds(prev => checked ? prev.filter(b => b !== id) : [...prev, id])}
                                            className="mt-0.5 h-4 w-4 accent-navy"
                                        />
                                        <div className="min-w-0">
                                            <div className="truncate text-sm font-medium text-ink">{rag.title || rag.name || `Book ${id}`}</div>
                                            {rag.course_names?.length > 0 && (
                                                <div className="mt-0.5 text-xs text-muted">{rag.course_names.join(', ')}</div>
                                            )}
                                        </div>
                                    </label>
                                )
                            })}
                        </div>
                        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-6 py-4">
                            <span className="font-mono text-xs text-muted">{selectedBookIds.length} book(s) selected</span>
                            <div className="flex gap-2">
                                <Button size="sm" variant="ghost" onClick={() => setBookPickerOpen(false)}>Cancel</Button>
                                <Button size="sm" disabled={selectedBookIds.length === 0} onClick={handleBookPickerConfirm}>
                                    Search selected books
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ExamProcessingPipeline
